import axios from 'axios';
import { isTokenExpired } from '../utils/tokenUtils';
import { API_BASE_URL } from '../config/constants';
import { refreshAccessTokenSingleFlight } from './tokenRefreshCoordinator';
import {
  isAuthCredentialRequest,
  resolve401DetailMessage,
  safeRedirectToLogin
} from './axiosAuthUtils';

// Синхронизация токенов между вкладками
if (typeof window !== 'undefined') {
  // Используем BroadcastChannel для синхронизации между вкладками (работает и в той же вкладке)
  let tokenBroadcastChannel = null;
  try {
    tokenBroadcastChannel = new BroadcastChannel('token_sync');
    tokenBroadcastChannel.onmessage = (e) => {
      if (e.data.type === 'token_updated') {
        const { token, refresh_token } = e.data;
        if (token) {
          localStorage.setItem('token', token);
        }
        if (refresh_token) {
          localStorage.setItem('refresh_token', refresh_token);
        }
        // Токен синхронизирован через localStorage/BroadcastChannel.
      }
    };
  } catch {
    /* BroadcastChannel может быть недоступен (SSR, политики браузера) */
  }
  
  // Также слушаем storage events (для старых браузеров)
  window.addEventListener('storage', (e) => {
    // Если токен обновился в другой вкладке, обновляем его здесь
    if (e.key === 'token' && e.newValue && e.newValue !== e.oldValue) {
      // Токен уже обновлен в localStorage другой вкладкой
      // Просто обновляем состояние, если нужно
      // Токен синхронизирован через localStorage/BroadcastChannel.
    }
    
    // Если refresh_token обновился
    // refresh_token при необходимости обрабатывается через тот же механизм, что и token выше
  });
  
  // Экспортируем функцию для отправки сообщений о обновлении токена
  window.broadcastTokenUpdate = (token, refresh_token) => {
    if (tokenBroadcastChannel) {
      tokenBroadcastChannel.postMessage({
        type: 'token_updated',
        token,
        refresh_token
      });
    }
  };
}

// Создаем базовый инстанс с общими настройками
const createApiInstance = (baseURL) => {
  // Используем абсолютный URL из констант, если он предоставлен, иначе относительный путь
  const baseApiUrl = API_BASE_URL 
    ? `${API_BASE_URL}/${baseURL}` 
    : `/api/${baseURL}`;
  
  const instance = axios.create({
    baseURL: baseApiUrl,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    withCredentials: true,
    maxRedirects: 0,
    validateStatus: function (status) {
      return status >= 200 && status < 400;
    }
  });

  // Добавляем интерцептор запросов
  instance.interceptors.request.use(async config => {
    let token = localStorage.getItem('token');

    // НЕ проверяем истечение токена перед каждым запросом - это слишком агрессивно
    // Проверка будет только при получении 401 ответа
    // Это позволяет работать в нескольких вкладках одновременно без конфликтов

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Дефолтный Content-Type: application/json заставляет axios 1.x сделать
    // JSON.stringify(formDataToJSON(FormData)): tags становится строкой (422),
    // а File превращается в {} — бинарник не уходит.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const headers = config.headers;
      if (headers && typeof headers.delete === 'function') {
        headers.delete('Content-Type');
        headers.delete('content-type');
      } else if (headers) {
        delete headers['Content-Type'];
        delete headers['content-type'];
      }
    }
    
    return config;
  });

  // Добавляем интерцептор ответов
  instance.interceptors.response.use(
    response => response,
    async error => {
      const originalRequest = error.config;
      
      // Обрабатываем ошибки
      if (error.response) {
        if (error.response.status === 401 && originalRequest) {
          const data401 = error.response.data || {};
          const msg401 = resolve401DetailMessage(data401);

          // Вход / регистрация: 401 — нормальный ответ, без refresh и без редиректа
          if (isAuthCredentialRequest(originalRequest)) {
            return Promise.reject({
              message: msg401,
              status: 401,
              data: data401,
              response: error.response,
              canceled: false
            });
          }

          if (!originalRequest._retry) {
            originalRequest._retry = true;
            try {
              const refresh_token = localStorage.getItem('refresh_token');
              if (!refresh_token || isTokenExpired(refresh_token)) {
                throw new Error('Refresh token expired or missing');
              }
              const newToken = await refreshAccessTokenSingleFlight();
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return instance(originalRequest);
            } catch {
              try {
                localStorage.setItem('lastTokenRemoval', Date.now().toString());
              } catch {
                /* ignore */
              }
              safeRedirectToLogin();
              return Promise.reject({
                message: 'Ошибка аутентификации. Пожалуйста, войдите снова.',
                status: 401,
                data: { auth_error: true, ...data401 },
                response: error.response,
                canceled: false
              });
            }
          }

          // Повторный 401 после refresh — сессия недействительна
          safeRedirectToLogin();
          return Promise.reject({
            message: 'Ошибка аутентификации. Пожалуйста, войдите снова.',
            status: 401,
            data: { auth_error: true, ...data401 },
            response: error.response,
            canceled: false
          });
        }

        // Текст ошибки: FastAPI отдаёт detail, не message
        const data = error.response.data || {};
        const msg =
          toReadableErrorMessage(data.detail, '') ||
          toReadableErrorMessage(data.message, '') ||
          toReadableErrorMessage(data.error, '');
        return Promise.reject({
          message: msg,
          status: error.response.status,
          data: data,
          response: error.response,
          canceled: false
        });
      } else if (error.request) {
        console.error('No response received:', error.request);
        return Promise.reject({
          message: 'Сервер не отвечает. Пожалуйста, попробуйте позже.',
          status: 0,
          canceled: false
        });
      } else {
        const isCanceled = error.message === 'canceled' || error.code === 'ERR_CANCELED' || error.name === 'AbortError';
        if (isCanceled) {
          return Promise.reject({ message: 'canceled', status: 0, canceled: true });
        }
        console.error('Request setup error:', error.message);
        return Promise.reject({
          message: 'Ошибка при выполнении запроса',
          status: 0,
          canceled: false
        });
      }
    }
  );

  return instance;
};

const toReadableErrorMessage = (value, fallback = 'Ошибка сервера') => {
  if (typeof value === 'string' && value.trim()) return value;
  if (Array.isArray(value)) {
    for (const item of value) {
      const msg = toReadableErrorMessage(item, '');
      if (msg) return msg;
    }
  }
  if (value && typeof value === 'object') {
    if (typeof value.message === 'string' && value.message.trim()) return value.message;
    if (typeof value.msg === 'string' && value.msg.trim()) return value.msg;
    if (typeof value.error === 'string' && value.error.trim()) return value.error;
    if (value.error && typeof value.error === 'object') {
      const nested = toReadableErrorMessage(value.error, '');
      if (nested) return nested;
    }
  }
  return fallback;
};

// Публичные префиксы шлюза (nginx / dev-server-proxy.cjs): /api/auth, /api/core, /api/files, /api/messages.
// Версия REST на core/message внутри сервисов — /api/v1; снаружи не ходить на /api/v1 (кроме прямого вызова сервиса).
export const authApi = createApiInstance('auth');
export const coreApi = createApiInstance('core');
export const filesApi = createApiInstance('files');
export const messageApi = createApiInstance('messages');

// Экспортируем authApi как дефолтный для обратной совместимости
export default authApi; 